import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckIcon, MinusIcon } from "lucide-react";
import { comparisonRows, pricingPlans } from "../../data/plans";
import { Segmented } from "../../components/ui/Segmented";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { spring } from "../../utils/motion";
function Cell({
  value
}: {
  value: boolean | string;
}) {
  if (value === true) return <CheckIcon className="mx-auto h-5 w-5 text-good" strokeWidth={3} aria-label="Included" data-id="element-1874" data-mp="j845vrh9mnhf" />;
  if (value === false) return <MinusIcon className="mx-auto h-5 w-5 text-ink-2" aria-label="Not included" data-id="element-1875" data-mp="xx0o2hd1dgvr" />;
  return <span className="text-[15px] font-semibold text-ink" data-id="element-1876" data-mp="t4hahkecmlqy">{value}</span>;
}
export function Pricing() {
  const [cycle, setCycle] = useState<"monthly" | "yearly">("yearly");
  const reduce = useReducedMotion() ?? false;
  return <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24" data-id="element-1877" data-mp="gl4tq6sxji1">
      <header className="text-center" data-id="element-1878" data-mp="1fvzowl8i92b2">
        <h1 className="text-[clamp(38px,6vw,72px)] font-bold leading-[1.05] tracking-[-0.02em]" data-id="element-1879" data-mp="1fuydgfnkudst">Start free. Stay because it works.</h1>
        <p className="mx-auto mt-5 max-w-[50ch] text-[17px] text-ink-2" data-id="element-1880" data-mp="1opmd9qadv3a8">Every plan includes all seven games and the translator that remembers.</p>
        <div className="mt-8 flex justify-center" data-id="element-1881" data-mp="1tnxsyhnd080d">
          <Segmented id="pricing-cycle" label="Billing cycle" value={cycle} onChange={setCycle} options={[{
          value: "monthly",
          label: "Monthly"
        }, {
          value: "yearly",
          label: "Yearly",
          badge: "-40%"
        }]} data-id="element-1882" data-mp="wygepeab9dn2" />
        </div>
      </header>

      <ul className="mt-14 grid items-stretch gap-6 md:grid-cols-3" data-id="element-1883" data-mp="1azj15s57wpyi">
        {pricingPlans.map((p, i) => {
        const price = cycle === "monthly" ? p.monthly : p.yearly;
        return <motion.li key={p.id} initial={reduce ? false : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} whileHover={reduce ? undefined : {
          y: -4
        }} transition={{
          ...spring.smooth,
          delay: reduce ? 0 : i * 0.06
        }} className={`flex flex-col rounded-panel border-3 border-line p-6 md:p-7 ${p.highlight ? "tex-dots bg-brand text-white shadow-brutal-lg" : "bg-surface text-ink shadow-brutal"}`} data-id="element-1884" data-mp="2mbjrvcln6ogw" data-mpi={i}>
              <div className="flex items-center justify-between" data-id="element-1885" data-mp="1dw51o2hzyd9y">
                <h2 className="text-2xl font-bold tracking-[-0.01em]" data-id="element-1886" data-mp="190y4ldj8g6u0">{p.name}</h2>
                {p.highlight && <span className="rounded-lg border-2 border-line bg-accent px-2 py-0.5 text-xs font-bold text-night" data-id="element-1887" data-mp="1npubh79jzdt1">Most picked</span>}
              </div>
              <p className={`mt-1 text-[15px] ${p.highlight ? "text-white/85" : "text-ink-2"}`} data-id="element-1888" data-mp="v7qa9e3rox8m">{p.tagline}</p>
              <div className="mt-6 flex h-14 items-baseline gap-1 overflow-hidden" data-id="element-1889" data-mp="q9ov349gpi2f">
                <AnimatePresence mode="popLayout" initial={false} data-id="element-1890" data-mp="31wilelteptld">
                  <motion.span key={`${p.id}-${price}`} initial={reduce ? {
                opacity: 0
              } : {
                y: 24,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} exit={reduce ? {
                opacity: 0
              } : {
                y: -24,
                opacity: 0
              }} transition={spring.bouncy} className="text-5xl font-bold tracking-[-0.03em]" data-id="element-1891" data-mp="31u5wfop8eiyt">
                    {price === 0 ? "€0" : `€${price}`}
                  </motion.span>
                </AnimatePresence>
                <span className={`text-sm ${p.highlight ? "text-white/80" : "text-ink-2"}`} data-id="element-1892" data-mp="3bmle0kwm95j6">{price === 0 ? "forever" : "/ month"}</span>
              </div>
              <ul className="mt-6 space-y-2.5" data-id="element-1893" data-mp="3fpqe6gt84v4n">
                {p.features.map(f => <li key={f} className="flex items-start gap-2.5 text-[15px]" data-id="element-1894" data-mp="205lkjlyj8knk">
                    <CheckIcon className={`mt-0.5 h-4 w-4 shrink-0 ${p.highlight ? "text-accent" : "text-good"}`} strokeWidth={3} aria-hidden="true" data-id="element-1895" data-mp="2499sw6uznk9v" />
                    {f}
                  </li>)}
              </ul>
              <div className="mt-auto pt-8" data-id="element-1896" data-mp="2fmtqqlp7834h">
                <BrutalButton to="/auth/signup" variant={p.highlight ? "accent" : "surface"} full data-id="element-1897" data-mp="2ap1ytrok0h0a">
                  {p.id === "free" ? "Start free" : p.id === "plus" ? "Try Plus for 7 days" : "Start Family trial"}
                </BrutalButton>
              </div>
            </motion.li>;
      })}
      </ul>

      <section aria-labelledby="compare-title" className="mt-24" data-id="element-1898" data-mp="2ygpflgs3qbou">
        <h2 id="compare-title" className="text-3xl font-bold tracking-[-0.02em] md:text-4xl" data-id="element-1899" data-mp="2tfry76ex1loe">
          Compare plans
        </h2>
        <div className="mt-6 overflow-x-auto rounded-card border-3 border-line bg-surface shadow-brutal" data-id="element-1900" data-mp="2pdy27bfo15uk">
          <table className="w-full min-w-[640px] text-left" data-id="element-1901" data-mp="1i2phmbctgw48">
            <thead className="border-b-3 border-line bg-sunken" data-id="element-1902" data-mp="1dz183n4mjqgc">
              <tr data-id="element-1903" data-mp="1e1x7lfw4en6w">
                <th scope="col" className="px-5 py-3 text-sm font-bold text-ink-2" data-id="element-1904" data-mp="3pn66hgp1rvw7">Feature</th>
                {pricingPlans.map(p => <th key={p.id} scope="col" className={`px-5 py-3 text-center text-sm font-bold ${p.highlight ? "text-brand" : "text-ink"}`} data-id="element-1905" data-mp="3tpay69vxqig7">
                    {p.name}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-1906" data-mp="c800088jsx8c">
              {comparisonRows.map(r => <tr key={r.feature} className="border-b-2 border-line/10 last:border-b-0" data-id="element-1907" data-mp="c7qcqsew146c">
                  <th scope="row" className="px-5 py-3.5 text-[15px] font-medium text-ink" data-id="element-1908" data-mp="h5jjbmk3s075">
                    {r.feature}
                  </th>
                  <td className="px-5 py-3.5 text-center" data-id="element-1909" data-mp="q7ixm73qgq6e"><Cell value={r.free} data-id="element-1910" data-mp="q6how9a6wfru" /></td>
                  <td className="bg-brand-tint/40 px-5 py-3.5 text-center" data-id="element-1911" data-mp="2n02nv0eoh10p"><Cell value={r.plus} data-id="element-1912" data-mp="2n1m3qpwf9a1y" /></td>
                  <td className="px-5 py-3.5 text-center" data-id="element-1913" data-mp="2rz5v8ri4rjbu"><Cell value={r.family} data-id="element-1914" data-mp="2s073ypbobtqe" /></td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-[15px] text-ink-2" data-id="element-1915" data-mp="3vc5t10pw0w7k">
          Questions about billing or schools?{" "}
          <Link to="/faq" className="font-semibold text-ink underline underline-offset-4" data-id="element-1916" data-mp="3qd1nql633imi">Read the FAQ</Link> or{" "}
          <Link to="/contact" className="font-semibold text-ink underline underline-offset-4" data-id="element-1917" data-mp="3690l0uh9soi">get in touch</Link>.
        </p>
      </section>
    </div>;
}